import Moment from 'moment-timezone';
import { i18n } from '@/plugins/i18n';
import { getRandomRGB } from '@/utils/main';
import { EventInfo } from '@/utils/meeting-utils';
import { RGBColors, valueKeyFormat } from '@/utils/constants';

// To appear in tops, teams must have a score greater than TopsMinBound for a criterion
export const TopsMinBound = 80;
// To appear in flops, teams must have a score lesser than FlopsMaxBound for a criterion
export const FlopsMaxBound = 20;
// The number of periods that will be calculated in total.
// Minimum value is 1 as the default displayed range must be calculated.
export const VHUB_NUMBER_OF_CALCULATED_PERIODS = 6;
// The number of periods required to properly calculate the scores of criteria.
// For example: The criterion about relativeProblemNumber needs to look back
// the five last periods to calculate an average
export const VHUB_TOTAL_NUMBER_OF_PERIODS = VHUB_NUMBER_OF_CALCULATED_PERIODS + 5;


/**
 * In Vitality hub, data is visualized from date to date (range).
 * If a range starts the first day of a month and ends the last, the previous range
 * should behave the same way.
 * @param  {Object} originalRange - { start: Moment, end: Moment }
 * @param  {Number} relativeShift - Number of ranges to shift
 * @return {Object} - { start: Moment, end: Moment }
 */
export function getRelativeRange(originalRange, relativeShift) {
  const { start, end } = originalRange;
  // Check relative number
  const shouldSubtract = relativeShift < 0;
  const shift = Math.abs(relativeShift);
  // Check if range is a month
  const isMonth = start.isSame(start.clone().startOf('month'), 'd') && end.isSame(end.clone().endOf('month'), 'd');

  let pStart;
  let pEnd;
  if (isMonth) {
    // Monthly period, keep the start at the beginning of the month, same for end
    const monthsDiff = end.diff(start, 'month');
    if (shouldSubtract) {
      pEnd = start.clone().subtract((shift - 1) * (monthsDiff + 1), 'month').subtract(1, 'day').endOf('day');
      pStart = pEnd.clone().subtract(monthsDiff, 'month').startOf('month');
    } else {
      pStart = end.clone().add((shift - 1) * (monthsDiff + 1), 'month').add(1, 'day').startOf('day');
      pEnd = pStart.clone().add(monthsDiff, 'month').endOf('month');
    }
  } else {
    // Not a monthly period, count the days gap
    const diff = end.diff(start, 'day') + 1;
    if (shouldSubtract) {
      pEnd = end.clone().subtract(shift * diff, 'day').endOf('day');
      pStart = start.clone().subtract(shift * diff, 'day').startOf('day');
    } else {
      pEnd = end.clone().add(shift * diff, 'day').endOf('day');
      pStart = start.clone().add(shift * diff, 'day').startOf('day');
    }
  }
  return { start: pStart, end: pEnd };
}


// Threshold with a specified divider to decrease the score according to the problem data
// Also the default threshold for 'getRecurrenceBreakdown'
const vcprThresholds = [
  { limit: 4, divider: 2 },
  { limit: 3, divider: 1.7 },
  { limit: 2, divider: 1.4 },
  { limit: 1, divider: 1.2 },
];

/**
 * Breakdown recurrence of an array of data
 * @param  {Array} items - Data that should be brokendown
 * @param  {String} recurrenceIdKey - Key of each item of items that determines the recurrence
 * @param  {String} recurrenceNameKey - Key of the value that should be used as the displayed text
 * @param  {Array} thresholds - Array of object with a 'limit' and 'divider' params to determine the
 *                              score according to the number of occurrences
 * @param  {Number} [defaultScore=0] - Score in case of no occurrences
 * TODO: Consider replacing score calculation with : 100 - <percentage_of_max_occurrences_outof_total_problems>
 *
 * @return {Object} - {
 *   score: number value calculated using the thresholds param and the number of occurrences of each recurrence
 *   maxOccurrences: the highest  number of occurrences amongst recurrences
 *   maxText: the text of the recurrence with most occcurrences
 *   itemsPerRecurrenceId: Object where the key is the value of recurrenceIdKey and the value is: {
 *     text: the displayed text for the recurrence
 *     occurrences: the number of occurrences of the recurrence
 *   }
 * }
 */
export function getRecurrenceBreakdown(
  items,
  recurrenceIdKey,
  recurrenceNameKey,
  thresholds = vcprThresholds,
  defaultScore = 0,
) {
  // Breakdown of recurrences per ID
  const itemsPerRecurrenceId = {};
  const itemsLen = items.length;
  for (let i = 0; i < itemsLen; i++) {
    const item = items[i];
    if (item[recurrenceIdKey]) {
      if (itemsPerRecurrenceId[`${item[recurrenceIdKey]}`] === undefined) {
        itemsPerRecurrenceId[`${item[recurrenceIdKey]}`] = {
          text: item[recurrenceNameKey],
          occurrences: 0,
        };
      }
      itemsPerRecurrenceId[`${item[recurrenceIdKey]}`].occurrences++;
    }
  }

  // Determine score using the number of recurrences and the number of occurrences per recurrence
  // And find the item with the most occurrences to highlight it
  let score = itemsLen === 0 ? defaultScore : 100;
  let maxOccurrences = 0;
  let maxText = '';
  const thresholdsLength = thresholds.length;
  const recurrences = Object.values(itemsPerRecurrenceId).sort((p1, p2) => p2.occurrences - p1.occurrences);
  for (let i = 0, len = recurrences.length; i < len; i++) {
    const r = recurrences[i];
    // Not a recurrence (break because previously sorted by array length)
    if (r.occurrences <= 1) {
      break;
    }
    if (i === 0) {
      maxOccurrences = r.occurrences;
      maxText = r.text;
    }
    // Divide score according to thresholds
    for (let j = 0; j < thresholdsLength; j++) {
      const threshold = thresholds[j];
      if (r.occurrences > threshold.limit) {
        score /= threshold.divider;
        break;
      }
    }
  }
  return { score, maxOccurrences, maxText, itemsPerRecurrenceId };
}


/**
 * Creates Chart.js datasets and labels from teams' recurrence data generated in 'getRecurrenceBreakdown'
 *
 * @param  {Array} teamGroups - See 'teamGroups' getter for more information
 * @param  {String} scoresKey - scoresKey of a criterion to know where to look for the recurrence data
 * @param  {String} [recDataKey='itemsPerRecurrenceId'] - key of the data created from getRecurrenceBreakdown
 * @param  {Array} colors - if set force the colors to be set according to the content of this array.
 *                          Each item is an object containing an 'r', 'g', 'b' prop between 0 and 255
 * @return {Object} Object with a 'datasets' and 'labels' property
 */
export function getLabelsAndDatasetsFromRecurrence(teamGroups, scoresKey, recDataKey = 'itemsPerRecurrenceId', colors) {
  const datasetProps = {
    type: 'bar',
    borderWidth: 2,
    pointBackgroundColor: 'white',
  };
  const mergedItemsPerRecId = {};
  const colorsSet = [];

  // Merge all teams data together
  const datasets = [];
  const groupsLen = teamGroups.length;
  for (let i = 0; i < groupsLen; i++) {
    const group = teamGroups[i];
    if (!group[scoresKey][0]) continue;
    // The result of the first period only will be displayed unlike other generic charts
    const itemsPerRecId = group[scoresKey][0].data[recDataKey];
    Object.keys(itemsPerRecId).forEach((recId) => {
      if (!mergedItemsPerRecId[recId]) {
        mergedItemsPerRecId[recId] = {
          id: recId,
          text: itemsPerRecId[recId].text,
          occurrences: 0,
        };
      }
      mergedItemsPerRecId[recId].occurrences += itemsPerRecId[recId].occurrences;
    });
    // Create the dataset
    const color = (colors && colors[i]) || getRandomRGB();
    colorsSet.push(color);
    datasets.push({
      ...datasetProps,
      backgroundColor: `rgba(${color.r}, ${color.g}, ${color.b}, 0.8)`,
      borderColor: `rgba(${color.r}, ${color.g}, ${color.b}, 1)`,
      label: group.name,
      data: [],
    });
  }
  const labels = [];
  // Sort by most occuring and fill the datasets
  const recurrences = Object.values(mergedItemsPerRecId).sort((p1, p2) => p2.occurrences - p1.occurrences);
  for (let i = 0, len = recurrences.length; i < len; i++) {
    const r = recurrences[i];
    for (let j = 0; j < groupsLen; j++) {
      const group = teamGroups[j];
      const itemsPerRecId = group[scoresKey][0] && group[scoresKey][0].data[recDataKey];
      datasets[j].data.push(itemsPerRecId[r.id] ? itemsPerRecId[r.id].occurrences : 0);
    }
    labels.push(r.text.length > 15 ? `${r.text.substring(0, 12)}...` : r.text);
  }
  return {
    labels,
    datasets,
    colorsSet,
  };
}

export const recurrenceChartOptions = {
  animation: { duration: 0 },
  scales: {
    yAxes: [{
      stacked: true,
    }],
    xAxes: [{
      stacked: true,
      offset: true,
    }],
  },
};


/**
 * Simple util function to add a key to an object.
 * Used in 'getPlanningsBreakdown'
 * @param  {Object} obj
 * @param  {String} key
 * @param  {Any} val
 */
function checkKeyExists(obj, key, val = {}) {
  if (!obj[key]) {
    obj[key] = val;
  }
}

const eiType = EventInfo.type;
const originDate = Moment('2017-01-01');

/**
 * Calculate a score based on plannings data and effectively completed meetings (or GW)
 * Create a key-value object from an array of plannings (meeting or GW plannings)
 * Where each key is a date within a range and each value is an array of planned meetings IDs
 * Example: {
 *   '2020-04-01': {
 *     <planning_id1>: 2,
 *     <planning_id2>: 3,
 *   },
 *   '2020-04-02': {
 *     <planning_id1>: 1,
 *     <planning_id2>: 1,
 *   },
 * }
 * 'Which means that there 2 plannings of meeting with planning id <planning_id1> on april 1st, ...
 *
 * @param  {Array} plannings - Array of meetings or GW plannings
 * @param  {Array} meetings - Array of completed meetings or GW
 * @param  {Object} range - Object with a start and end property which are Moment objects
 * @return {Object} - Object containing score, nTotalExpected, nTotalExpectedDone, nTotalExpectedNotDone
 */
export function getPlanningsBreakdown(plannings, meetings, range, planningRefKey = 'gw', refKey = 'template') {
  // ===========================
  // Check all expected meetings
  // ===========================
  // Expected meeting id occurrences per date of the current period
  // Key = date, value = [<planned_meeting_id1>, <planned_meeting_id2>, ...]
  const planningsPerDate = {};
  let nTotalExpected = 0;
  // Object of all the dates where an event is planned : { <planning_id>: { date1: true, date2: true } }
  const datesPerPlanning = {};
  const planningsPerGw = {};

  for (let i = 0, len = plannings.length; i < len; i++) {
    const planning = plannings[i];
    let mCreation = Moment(planning.created);
    if (!mCreation.isValid()) {
      mCreation = Moment(originDate);
    }
    if (mCreation.isAfter(range.end)) {
      // Planning was created after the end of the period
      continue;
    }
    checkKeyExists(planningsPerGw, `${planning[planningRefKey]}`, []);
    planningsPerGw[`${planning[planningRefKey]}`].push(planning.id);

    const { repeatType, repeatData } = planning;
    const iStart = Moment(range.start);
    switch (repeatType) {
      case eiType.daily:
        // Check every day of the period
        while (!iStart.isAfter(range.end)) {
          // Idx of the days array of repeatData
          const dayOfWeek = iStart.isoWeekday() - 1;
          if (iStart.isSameOrAfter(mCreation, 'd') && repeatData[dayOfWeek] && repeatData[dayOfWeek].on) {
            const key = iStart.format(valueKeyFormat);
            checkKeyExists(planningsPerDate, key, { nTotalExpected: 0 });
            planningsPerDate[key].nTotalExpected++;
            checkKeyExists(planningsPerDate[key], 'expected', {});
            checkKeyExists(planningsPerDate[key].expected, `${planning[planningRefKey]}-${planning.team}`, 0);
            checkKeyExists(planningsPerDate[key], 'expectedDone', {});
            checkKeyExists(planningsPerDate[key].expectedDone, `${planning[planningRefKey]}-${planning.team}`, 0);
            planningsPerDate[key].expected[`${planning[planningRefKey]}-${planning.team}`]++;
            checkKeyExists(datesPerPlanning, `${planning.id}`, {});
            datesPerPlanning[`${planning.id}`][key] = { carriedOut: false };
            nTotalExpected++;
          }
          iStart.add(1, 'day');
        }
        break;
      case eiType.weekly:
        // Set the date to the next weekly occurrence (0: monday, 1: tuesday, ...)
        iStart.day((repeatData.day + 1) % 7);
        while (!iStart.isAfter(range.end)) {
          if (iStart.isSameOrAfter(mCreation, 'd')) {
            const key = iStart.format(valueKeyFormat);
            checkKeyExists(planningsPerDate, key, { nTotalExpected: 0 });
            planningsPerDate[key].nTotalExpected++;
            checkKeyExists(planningsPerDate[key], 'expected', {});
            checkKeyExists(planningsPerDate[key].expected, `${planning[planningRefKey]}-${planning.team}`, 0);
            checkKeyExists(planningsPerDate[key], 'expectedDone', {});
            checkKeyExists(planningsPerDate[key].expectedDone, `${planning[planningRefKey]}-${planning.team}`, 0);
            planningsPerDate[key].expected[`${planning[planningRefKey]}-${planning.team}`]++;
            checkKeyExists(datesPerPlanning, `${planning.id}`, {});
            datesPerPlanning[`${planning.id}`][key] = { carriedOut: false };
            nTotalExpected++;
          }
          iStart.add(1, 'week');
        }
        break;
      case eiType.monthly:
        // Set the date to the next monthly occurrence
        if (iStart.date() <= repeatData.day) {
          iStart.date(repeatData.day);
        } else {
          iStart.add(1, 'month').date(repeatData.day);
        }
        while (!iStart.isAfter(range.end)) {
          if (iStart.isSameOrAfter(mCreation, 'd')) {
            const key = iStart.format(valueKeyFormat);
            checkKeyExists(planningsPerDate, key, { nTotalExpected: 0 });
            planningsPerDate[key].nTotalExpected++;
            checkKeyExists(planningsPerDate[key], 'expected', {});
            checkKeyExists(planningsPerDate[key].expected, `${planning[planningRefKey]}-${planning.team}`, 0);
            checkKeyExists(planningsPerDate[key], 'expectedDone', {});
            checkKeyExists(planningsPerDate[key].expectedDone, `${planning[planningRefKey]}-${planning.team}`, 0);
            planningsPerDate[key].expected[`${planning[planningRefKey]}-${planning.team}`]++;
            checkKeyExists(datesPerPlanning, `${planning.id}`, {});
            datesPerPlanning[`${planning.id}`][key] = { carriedOut: false };
            nTotalExpected++;
          }
          iStart.add(1, 'month');
        }
        break;
      default: {
        // Check that the occurrence is within the period
        const customDate = Moment(repeatData.day);
        if (range.start.isBefore(customDate) && range.end.isAfter(customDate)) {
          const key = customDate.format(valueKeyFormat);
          checkKeyExists(planningsPerDate, key, { nTotalExpected: 0 });
          planningsPerDate[key].nTotalExpected++;
          checkKeyExists(planningsPerDate[key], 'expected', {});
          checkKeyExists(planningsPerDate[key].expected, `${planning[planningRefKey]}-${planning.team}`, 0);
          checkKeyExists(planningsPerDate[key], 'expectedDone', {});
          checkKeyExists(planningsPerDate[key].expectedDone, `${planning[planningRefKey]}-${planning.team}`, 0);
          planningsPerDate[key].expected[`${planning[planningRefKey]}-${planning.team}`]++;
          checkKeyExists(datesPerPlanning, `${planning.id}`, {});
          datesPerPlanning[`${planning.id}`][key] = { carriedOut: false };
          nTotalExpected++;
        }
        break;
      }
    }
  }


  // ================================================
  // Check completed meetings number matches expected
  // ================================================
  // Number of expected and completed meetings during this period
  let nTotalExpectedDone = 0;
  const filtered = meetings.filter(m => range.start.isBefore(m.date) && range.end.isAfter(m.date));
  for (let i = 0, len = filtered.length; i < len; i++) {
    const meeting = filtered[i];
    const mDate = Moment(meeting.date);
    const key = mDate.format(valueKeyFormat);
    const mKey = `${meeting[refKey]}`;
    if (planningsPerGw[mKey]) {
      // Check if there was a planning for this event. If so, check it as 'carried out'
      const planningId = planningsPerGw[mKey].find(pId => datesPerPlanning[`${pId}`]
        && datesPerPlanning[`${pId}`][key]
        && datesPerPlanning[`${pId}`][key].carriedOut === false);
      if (planningId) {
        datesPerPlanning[`${planningId}`][key].carriedOut = true;
      }
    }
    if (planningsPerDate[key] && planningsPerDate[key].expected[`${mKey}-${meeting.team}`]) {
      planningsPerDate[key].expectedDone[`${mKey}-${meeting.team}`]++;
      nTotalExpectedDone++;
      planningsPerDate[key].nTotalDone = (planningsPerDate[key].nTotalDone || 0) + 1;
    }
  }

  // ===========================================
  // Check remaining number of expected meetings
  // ===========================================
  // Number of remaining expected meetings not done during this period
  let nTotalExpectedNotDone = 0;
  Object.keys(planningsPerDate).forEach((key) => {
    Object.keys(planningsPerDate[key].expected).forEach((mKey) => {
      if (planningsPerDate[key].expected[mKey] > 0) {
        nTotalExpectedNotDone += planningsPerDate[key].expected[mKey] - (planningsPerDate[key].expectedDone[mKey] || 0);
      }
    });
  });
  return {
    planningsPerDate, nTotalExpected, nTotalExpectedDone, nTotalExpectedNotDone, datesPerPlanning,
  };
}

/**
 * Basic score chart options
 * @param  {Object} [criterion=null] - Vitality criterion if this chart refers to a criterion
 * @return {Object} Chart.js options
 */
export function defaultChartOptions(criterion = null, hideLegend = false) {
  return {
    responsive: false,
    legend: { display: !hideLegend },
    animation: { duration: 0 },
    scales: {
      yAxes: [{
        ticks: {
          beginAtZero: true,
        },
      }],
    },
    tooltips: {
      callbacks: {
        title: ([item], chartData) => {
          const teamName = chartData.datasets[item.datasetIndex].label;
          return `${teamName} (${item.label})`;
        },
        label: (item, chartData) => {
          const scoreData = chartData.datasets[item.datasetIndex].scoreData[item.index];
          const score = `${i18n.t('message.VH_SCORE')} ${i18n.t('message.TWO_DOTS')} ${item.value}`;
          return criterion
            ? `${score}\n${criterion.text(scoreData)}`
            : score;
        },
      },
    },
  };
}


export const defaultDatasetProps = {
  type: 'line',
  tension: 0,
  borderWidth: 2,
  backgroundColor: 'transparent',
  pointBackgroundColor: 'white',
  pointRadius: 4,
  pointHitRadius: 7,
  pointHoverRadius: 7,
};


// Thresholds of vitality scores
const { green, red, yellow, orange } = RGBColors;
export const vitalityThresholds = [
  {
    id: 0,
    titleTr: 'message.VH_CRIT_BAD',
    score: 25,
    color: `rgb(${red.r}, ${red.g}, ${red.b})`,
    lightColor: `rgba(${red.r}, ${red.g}, ${red.b}, 0.5)`,
    lighterColor: `rgba(${red.r}, ${red.g}, ${red.b}, 0.15)`,
  },
  {
    id: 1,
    titleTr: 'message.VH_CRIT_INADEQUATE',
    score: 50,
    color: `rgb(${orange.r}, ${orange.g}, ${orange.b})`,
    lightColor: `rgba(${orange.r}, ${orange.g}, ${orange.b}, 0.5)`,
    lighterColor: `rgba(${orange.r}, ${orange.g}, ${orange.b}, 0.15)`,
  },
  {
    id: 2,
    titleTr: 'message.VH_CRIT_ACCEPTABLE',
    score: 75,
    color: `rgb(${yellow.r}, ${yellow.g}, ${yellow.b})`,
    lightColor: `rgba(${yellow.r}, ${yellow.g}, ${yellow.b}, 0.5)`,
    lighterColor: `rgba(${yellow.r}, ${yellow.g}, ${yellow.b}, 0.15)`,
  },
  {
    id: 3,
    titleTr: 'message.VH_CRIT_GOOD',
    score: 100,
    color: `rgb(${green.r}, ${green.g}, ${green.b})`,
    lightColor: `rgba(${green.r}, ${green.g}, ${green.b}, 0.5)`,
    lighterColor: `rgba(${green.r}, ${green.g}, ${green.b}, 0.15)`,
  },
];


/**
 * Returns the quantile of an array of numbers
 * Credits: https://stackoverflow.com/questions/48719873/how-to-get-median-and-
 * quartiles-percentiles-of-an-array-in-javascript-or-php
 * @param  {Array} arr Array of numbers
 * @param  {Number} q  Percentage of the value to return
 * @return {Number}    Item of the aray
 */
export function quantile(arr, q) {
  const sorted = arr.sort((a, b) => a - b);
  const pos = (sorted.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
}

/**
 * Return outliers from an array of numeric values
 * @param  {Array} values  Array of Numbers
 * @return {Array}        Array of outliying values
 */
export function findOutliers(values) {
  const q1 = quantile(values, 0.25);
  const q3 = quantile(values, 0.75);
  const infLimit = quantile(values, 0.25) - 1.5 * (q3 - q1);
  const supLimit = quantile(values, 0.75) + 1.5 * (q3 - q1);
  return values.filter(val => val < infLimit || val > supLimit);
}

export default {
  TopsMinBound,
  FlopsMaxBound,
  getRelativeRange,

  getRecurrenceBreakdown,
  getLabelsAndDatasetsFromRecurrence,
  recurrenceChartOptions,

  getPlanningsBreakdown,

  defaultChartOptions,
  defaultDatasetProps,

  vitalityThresholds,

  quantile,
  findOutliers,

};
